import axios from '../helpers/axios'
import { logError } from '../services';
import qs from 'querystring';
import {
  IClient,
  IClientDetail,
  IClientSource,
  IClientStatus,
  IResponse,
  IDropdownResponse,
  IClientBuild,
  IPeopleSimple,
  IClientAgent,
  IExternalAgent
} from '../models';

export const getClients = async (filters?: {
  page?: number;
  perPage: number | string;
  sortBy?: string;
  sortDirection?: string;
  search?: string;
  includeComplete?: boolean;
  isActive?: boolean;
  includeHiddenPurchasers?: boolean;
}): Promise<IResponse<IClient[]>> => {
  try {
    const { data } = await axios.get('/api/clients', {
      params: filters,
      paramsSerializer: params => qs.stringify(params)
    });
    return data;
  } catch (error) {
    logError(error, 'getClients failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getClientDetail = async (id: string): Promise<IClientDetail> => {
  try {
    const { data } = await axios.get(`/api/clients/${id}`);
    return data;
  } catch (error) {
    logError(error, 'getClientDetail failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getClientAgents = async (query?: string, externalAgentId?: number): Promise<IClientAgent[]> => {
  try {
    const { data } = await axios.get('/api/clients/external-agents', {
      params: { query, externalAgentId },
      paramsSerializer: params => qs.stringify(params),
    });
    return data;
  } catch (error) {
    logError(error, 'getClientAgents failed');
    return Promise.reject(error?.response?.data);
  }
};

export const addExternalAgent = async (agentDetails: IExternalAgent): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/clients/external-agent`, agentDetails);
    return data;
  } catch (error) {
    logError(error, 'addExternalAgent failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getClientSources = async (filters?: {
  page?: number;
  perPage: number;
  sortBy?: string;
  sortDirection?: string;
  name?: string;
}): Promise<IResponse<IClientSource[]>> => {
  try {
    const { data } = await axios.get(`/api/client-sources`, {
      params: filters,
      paramsSerializer: params => qs.stringify(params)
    });
    return data;
  } catch (error) {
    logError(error, 'getClientSources failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getClientStatuses = async (filters?: {
  page?: number;
  perPage: number;
  sortBy?: string;
  sortDirection?: string;
  search?: string;
}): Promise<IResponse<IClientStatus[]>> => {
  try {
    const { data } = await axios.get(`/api/clients/statuses`, {
      params: filters,
      paramsSerializer: params => qs.stringify(params)
    });
    return data;
  } catch (error) {
    logError(error, 'getClientStatuses failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createClient = async (options: IClientDetail) => {
  try {
    const result = await axios.post('/api/clients', options);
    return result.data;
  } catch (error) {
    logError(
      {
        error,
        payload: {
          ...options
        }
      },
      'createClient failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const createLoginForAdditionalContact = async (Id: number) => {
  try {
    const result = await axios.post(`/api/clients/login/${Id}`);
    return result.data;
  } catch (error) {
    logError(
      {
        error
      },
      'createLoginForAdditionalContact failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const updateClient = async (id: string, options: IClientDetail) => {
  try {
    const result = await axios.put(`/api/clients/${id}`, options);
    return result.data;
  } catch (error) {
    logError(
      {
        error,
        payload: {
          ...options
        }
      },
      'updateClient failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const getClientDemographicsReferral = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get('/api/clients/demographics/referral-sources');
    return data;
  } catch (error) {
    logError(error, 'getClientDemographicsReferral failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getClientDemographicsFamilyStatus = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get('/api/clients/demographics/family-statuses');
    return data;
  } catch (error) {
    logError(error, 'getClientDemographicsFamilyStatus failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getClientDemographicsSocialMedia = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get('/api/clients/demographics/social-media');
    return data;
  } catch (error) {
    logError(error, 'getClientDemographicsSocialMedia failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getClientDemographicsAgeRange = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get('/api/clients/demographics/age-range');
    return data;
  } catch (error) {
    logError(error, 'getClientDemographicsAgeRange failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getClientDemographicsEducationLevel = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get('/api/clients/demographics/education-levels');
    return data;
  } catch (error) {
    logError(error, 'getClientDemographicsEducationLevel failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getClientDemographicsIncomeLevels = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get('/api/clients/demographics/income-levels');
    return data;
  } catch (error) {
    logError(error, 'getClientDemographicsIncomeLevels failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getClientDemographicsCommunicationsMethods = async (): Promise<
  IDropdownResponse[]
> => {
  try {
    const { data } = await axios.get('/api/clients/demographics/communications-methods');
    return data;
  } catch (error) {
    logError(error, 'getClientDemographicsCommunicationsMethods failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getClientBuilds = async (clientId: number, filters?: {
  page?: number;
  perPage: number;
  sortBy?: string;
  sortDirection?: string;
  personId?: number;
}): Promise<IResponse<IClientBuild[]>> => {
  try {
    const { data } = await axios.get(`/api/clients/${clientId}/builds`, {
      params: filters,
      paramsSerializer: params => qs.stringify(params)
    });
    return data;
  } catch (error) {
    logError(error, 'getClientBuilds failed');
    return Promise.reject(error?.response?.data);
  }
};

export const sendClientWelcomeEmail = async (personId: number): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/clients/${personId}/activation`);
    return data;
  } catch (error) {
    logError(error, 'sendClientWelcomeEmail failed');
    return Promise.reject(error?.response?.data);
  }
};

export const lookupAdditionalContactUser = async (additionalContactId: number, email: string): Promise<number> => {
  try {
    const { data } = await axios.get(`/api/clients/additional-contact/${additionalContactId}/lookup-user`, {
      params: { email },
      paramsSerializer: params => qs.stringify(params)
    });
    return data;
  } catch (error) {
    logError(error, 'lookupAdditionalContactUser failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createAdditionalContactUser = async (additionalContactId: number, additionalUserId: number): Promise< boolean> => {
  try {
    const { data } = await axios.put(`/api/clients/additional-contact/${additionalContactId}/assign-user/${additionalUserId}`);
    return data;
  } catch (error) {
    logError(error, 'createAdditionalContactUser failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getClientsSimple = async (): Promise<IResponse<IPeopleSimple[]>> => {
  try {
    const { data } = await axios.get('/api/people/lookup-simple', {
      params: { personType: 'Purchaser', isActive: true }
    });
    return data;
  } catch (error) {
    logError(
      {
        error
      },
      'getClientsSimple failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const resetClientPassword = async (personId: number): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/clients/${personId}/reset-password`);
    return data;
  } catch (error) {
    logError(error, 'resetClientPassword failed');
    return Promise.reject(error?.response?.data);
  }
};
